'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface ToxicSubstance {
  id: number;
  name: string;
  category: string;
  toxicityLevel: 'high' | 'moderate' | 'low';
  symptoms: string[];
  immediateActions: string[];
  image: string;
  alt: string;
}

interface PoisonControlDatabaseProps {
  substances: ToxicSubstance[];
}

const PoisonControlDatabase: React.FC<PoisonControlDatabaseProps> = ({ substances }) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSubstance, setSelectedSubstance] = useState<ToxicSubstance | null>(null);

  const categories = ['All', ...Array.from(new Set(substances.map(s => s.category)))];

  const filteredSubstances = substances.filter(substance => {
    const matchesCategory = selectedCategory === 'All' || substance.category === selectedCategory;
    const matchesSearch = substance.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const toxicityColors = {
    high: 'bg-error text-white',
    moderate: 'bg-warning text-foreground',
    low: 'bg-primary text-white'
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="p-6 border-b border-border">
        <h3 className="text-2xl font-headline font-bold text-foreground mb-4 flex items-center space-x-2">
          <Icon name="ShieldExclamationIcon" size={28} variant="solid" className="text-error" />
          <span>Toxic Substances Database</span>
        </h3>

        <div className="space-y-4">
          <div className="relative">
            <Icon 
              name="MagnifyingGlassIcon" 
              size={20} 
              variant="outline" 
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <input
              type="text"
              placeholder="Search toxic substances..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-lg font-semibold transition-all duration-300 ${
                  selectedCategory === category
                    ? 'bg-primary text-white' :'bg-muted text-foreground hover:bg-muted/80'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 p-6">
        {filteredSubstances.map((substance) => (
          <div
            key={substance.id}
            onClick={() => setSelectedSubstance(substance)}
            className="border border-border rounded-lg overflow-hidden cursor-pointer hover:shadow-lg transition-all duration-300"
          >
            <div className="relative h-48 overflow-hidden">
              <AppImage
                src={substance.image}
                alt={substance.alt}
                className="w-full h-full object-cover"
              />
              <div className={`absolute top-2 right-2 px-3 py-1 rounded-full text-xs font-bold ${toxicityColors[substance.toxicityLevel]}`}>
                {substance.toxicityLevel.toUpperCase()}
              </div>
            </div>
            <div className="p-4">
              <h4 className="text-lg font-headline font-bold text-foreground mb-1">{substance.name}</h4>
              <p className="text-sm text-muted-foreground">{substance.category}</p>
            </div>
          </div>
        ))}
      </div>

      {selectedSubstance && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
              <h3 className="text-2xl font-headline font-bold text-foreground">{selectedSubstance.name}</h3>
              <button
                onClick={() => setSelectedSubstance(null)}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
              >
                <Icon name="XMarkIcon" size={24} variant="outline" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="relative h-64 rounded-lg overflow-hidden">
                <AppImage
                  src={selectedSubstance.image}
                  alt={selectedSubstance.alt}
                  className="w-full h-full object-cover"
                />
              </div>

              <div>
                <div className="flex items-center space-x-3 mb-4">
                  <span className="text-sm font-semibold text-muted-foreground">Category:</span>
                  <span className="px-3 py-1 bg-muted text-foreground rounded-full text-sm font-semibold">
                    {selectedSubstance.category}
                  </span>
                  <span className={`px-3 py-1 rounded-full text-sm font-bold ${toxicityColors[selectedSubstance.toxicityLevel]}`}>
                    {selectedSubstance.toxicityLevel.toUpperCase()} TOXICITY
                  </span>
                </div>
              </div>

              <div>
                <h4 className="text-lg font-headline font-semibold text-foreground mb-3 flex items-center space-x-2">
                  <Icon name="ExclamationCircleIcon" size={20} variant="solid" className="text-error" />
                  <span>Symptoms of Poisoning</span>
                </h4>
                <ul className="space-y-2">
                  {selectedSubstance.symptoms.map((symptom, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <Icon name="ChevronRightIcon" size={16} variant="solid" className="text-primary flex-shrink-0 mt-1" />
                      <span className="text-foreground">{symptom}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-error/10 border-2 border-error rounded-lg p-4">
                <h4 className="text-lg font-headline font-semibold text-error mb-3 flex items-center space-x-2">
                  <Icon name="BoltIcon" size={20} variant="solid" />
                  <span>Immediate Actions</span>
                </h4>
                <ol className="space-y-2">
                  {selectedSubstance.immediateActions.map((action, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-error text-white rounded-full flex items-center justify-center text-sm font-bold">
                        {index + 1}
                      </span>
                      <span className="text-foreground">{action}</span>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="bg-warning/10 border border-warning rounded-lg p-4">
                <p className="text-foreground font-semibold flex items-start space-x-2">
                  <Icon name="PhoneIcon" size={20} variant="solid" className="text-warning flex-shrink-0 mt-0.5" />
                  <span>Call Poison Control immediately at 1-888-POISON-1 or your emergency veterinarian</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PoisonControlDatabase;